/**
 * StampDateTime — iter303ak-fix67 · F1 (P0 · reviewer-informed)
 *
 * Purpose: Real behaviour for the `stamp_datetime` primitive.
 * Previously fell through to GenericFallback (blank ack card) so the
 * 4 workflow usages (Deposit received, Materials delivery, Job start/end)
 * were silently discarding the timestamp the operator was "capturing".
 *
 * Behaviour: Renders a button that captures `Date.now()` in ISO format,
 * writes it via onChange to the primitive's `write_to` key, and advances.
 * Also shows an editable date+time input so the operator can back-date
 * if the moment they clicked isn't the moment they want to record.
 *
 * Params:
 *   label        · button label · default "Stamp now"
 *   header_title · card header
 *   write_to     · target key (dotted or flat) — required
 *   allow_edit   · "true" (default) to expose the date+time input
 */
import React, { useState, useMemo } from 'react';
import { Clock, CheckCircle2 } from 'lucide-react';

// Local ISO helper — includes local TZ offset so the stamp reflects what
// the operator saw on their clock, not UTC. Format:
// "2026-09-05T14:32:00+12:00" — parseable by every downstream consumer.
function localIsoNow() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = now.getFullYear();
  const mm   = pad(now.getMonth() + 1);
  const dd   = pad(now.getDate());
  const hh   = pad(now.getHours());
  const mi   = pad(now.getMinutes());
  const ss   = pad(now.getSeconds());
  const offMin = -now.getTimezoneOffset();
  const offSign = offMin >= 0 ? '+' : '-';
  const offHh = pad(Math.floor(Math.abs(offMin) / 60));
  const offMm = pad(Math.abs(offMin) % 60);
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}${offSign}${offHh}:${offMm}`;
}

// Convert an ISO string to the `datetime-local` input format
// (YYYY-MM-DDTHH:MM), which drops the seconds + tz.
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function StampDateTime({ action, value, onChange, onNext, onBack, canBack }) {
  const params = action.params || {};
  const label       = params.label || 'Stamp now';
  const headerTitle = params.header_title || 'Timestamp';
  const allowEdit   = String(params.allow_edit ?? 'true').toLowerCase() !== 'false';

  const [stamp, setStamp] = useState(value || '');
  const stampedLabel = useMemo(() => {
    if (!stamp) return '';
    const d = new Date(stamp);
    if (isNaN(d.getTime())) return stamp;
    return d.toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' });
  }, [stamp]);

  const doStamp = () => {
    const now = localIsoNow();
    setStamp(now);
    onChange(now);
  };

  const onEdit = (localValue) => {
    // Rebuild ISO from datetime-local input.
    if (!localValue) { setStamp(''); onChange(''); return; }
    const d = new Date(localValue);
    if (isNaN(d.getTime())) return;
    // Preserve seconds=0 from the input.
    const pad = (n) => String(n).padStart(2, '0');
    const offMin = -d.getTimezoneOffset();
    const offSign = offMin >= 0 ? '+' : '-';
    const offHh = pad(Math.floor(Math.abs(offMin) / 60));
    const offMm = pad(Math.abs(offMin) % 60);
    const iso = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`
              + `T${pad(d.getHours())}:${pad(d.getMinutes())}:00${offSign}${offHh}:${offMm}`;
    setStamp(iso);
    onChange(iso);
  };

  return (
    <div className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm space-y-4"
         data-testid="wf-stamp-datetime">
      <div className="flex items-center gap-2 text-slate-800">
        <Clock className="w-5 h-5" />
        <h3 className="font-semibold">{headerTitle}</h3>
      </div>

      {!stamp && (
        <button
          onClick={doStamp}
          data-testid="wf-stamp-btn"
          className="w-full px-6 py-4 border-2 border-indigo-500 rounded font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
        >
          {label}
        </button>
      )}

      {stamp && (
        <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 flex items-start gap-2"
             data-testid="wf-stamp-set">
          <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-600 shrink-0" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Stamped</div>
            <div className="text-sm font-medium text-slate-800">{stampedLabel}</div>
            {allowEdit && (
              <div className="mt-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">
                  Edit if needed
                </label>
                <input
                  type="datetime-local"
                  value={isoToLocalInput(stamp)}
                  onChange={(e) => onEdit(e.target.value)}
                  data-testid="wf-stamp-edit-input"
                  className="px-2 py-1 text-sm border border-slate-300 rounded bg-white"
                />
              </div>
            )}
          </div>
          <button
            onClick={() => { setStamp(''); onChange(''); }}
            className="text-xs text-slate-500 hover:text-rose-600"
            data-testid="wf-stamp-clear"
          >Clear</button>
        </div>
      )}

      <div className="flex justify-between gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-stamp-back"
            className="px-4 py-2 rounded text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">
            Back
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!stamp}
          data-testid="wf-stamp-next"
          className="ml-auto px-6 py-2 rounded font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
